import type { FontFamilyPresetPair, ThemeFont, ThemeFontPreset } from '@sites/site-theme/src/types/theme-font.type';

import { hashCode } from '@common/helpers/hash';

import { ThemeFontStyle, ThemeTextTransform } from '@sites/site-theme/src/enums/css.enum';
import { ThemeFontTags } from '@sites/site-theme/src/enums/font-tags.enum';
import { FONT_TAGS_ORDER } from '@sites/site-theme/src/config/vocabularies.const';
import { ThemeFontWeights } from '@sites/site-theme/src/enums/font-families.enum';
import { FONT_FAMILIES, FONT_WEIGHT_VALUES, fontStyleDefaults } from '@sites/site-theme/src/config/fonts.const';
import { onDesktop } from '@sites/site-theme/src/helpers/responsive';

import { DEFAULT_FONT_FAMILY_PRESET_PAIRS } from '@sites/site-theme/src/config/presets';

function buildFont(tag: ThemeFontTags, presetPairs: FontFamilyPresetPair): ThemeFont {
    // Body copy takes the pair's paragraph face; everything else — headings, buttons, the quote —
    // takes its header face, and only the quote is set in italic.
    const family = tag === ThemeFontTags.p ? presetPairs.paragraph : presetPairs.header;
    const weight = tag === ThemeFontTags.p ? ThemeFontWeights.normal : ThemeFontWeights.bold;
    const style = tag === ThemeFontTags.quote ? ThemeFontStyle.italic : ThemeFontStyle.normal;

    const defaults = fontStyleDefaults(tag);

    return {
        tag,
        font: family,
        fontFamily: onDesktop(FONT_FAMILIES[family].stack),
        fontSize: onDesktop(defaults?.fontSize),
        fontWeight: onDesktop(FONT_WEIGHT_VALUES[weight]),
        fontStyle: onDesktop(style),
        lineHeight: onDesktop(defaults?.lineHeight),
        textTransform: onDesktop(ThemeTextTransform.none),
        letterSpacing: onDesktop(defaults?.letterSpacing),
    };
}

export function getThemeFontPresets(): ThemeFontPreset[] {
    return DEFAULT_FONT_FAMILY_PRESET_PAIRS.map((presetPairs: FontFamilyPresetPair) => ({
        index: hashCode(presetPairs),
        // In the order the settings panel lists them, minus `pre`: the website variant has no
        // preformatted-text tag to style, and it follows body copy rather than carrying a preset.
        values: FONT_TAGS_ORDER.filter((tag) => tag !== ThemeFontTags.pre).map((tag) => buildFont(tag, presetPairs)),
    }));
}
